<script lang="ts" setup>
import { ref } from 'vue'
import { EventUsersFeedback } from '@/enitites/event/model/types'
import { useImage } from '@/shared/composables/image'
import IconLike from '@/shared/icons/IconLike.vue'
import IconMinus from '@/shared/icons/IconMinus.vue'
import IconPlus from '@/shared/icons/IconPlus.vue'
import { EVENT_STAR_CONFIG } from '../constants'

const props = defineProps<{
  feedback: EventUsersFeedback
  withLine: boolean
}>()

const showMore = ref(false)

const RATING_CATEGORIES = [
  { key: 'timeliness', label: 'Timeliness', value: props.feedback.timeliness },
  { key: 'communication', label: 'Communication', value: props.feedback.communication },
  { key: 'venue', label: 'Venue', value: props.feedback.venue },
  { key: 'vibe', label: 'Vibe', value: props.feedback.vibe },
  { key: 'musicSound', label: 'Music / Sound', value: props.feedback.musicSound },
] as const
const { getAvatar } = useImage()

function formatDate(dateString: string | Date) {
  const date = new Date(dateString)
  const day = String(date.getDate()).padStart(2, '0')
  const month = String(date.getMonth() + 1).padStart(2, '0') // Месяцы с 0
  const year = date.getFullYear()
  return `${day}.${month}.${year}`
}
</script>

<template>
  <div class="wrapper">
    <div class="container">
      <div class="userInfo">
        <div class="imageContainer">
          <v-avatar
            class="image"
            left
            size="48"
          >
            <v-img
              :src="getAvatar(feedback?.avatar, 48)"
              :alt="feedback.dancerName"
            />
          </v-avatar>
          <div
            v-if="feedback?.isLiked"
            class="like"
          >
            <IconLike />
          </div>
        </div>
        <div>
          <div class="name">{{ feedback?.dancerName }}</div>
          <div class="date">{{ formatDate(feedback?.createdAt) }}</div>
        </div>
      </div>
      <div class="content">
        <div class="items">
          <div class="header">
            <div>
              <v-rating
                v-bind="EVENT_STAR_CONFIG"
                :value="feedback.averageRating"
                readonly
              />
            </div>
            <button @click="showMore = !showMore">
              {{ showMore ? 'Show less' : 'Show more' }}
              <IconMinus
                v-if="showMore"
                color="black"
                :without-circle="true"
              />
              <IconPlus
                v-else
                color="black"
                :without-circle="true"
              />
            </button>
          </div>
          <template v-if="showMore">
            <div
              v-for="rating in RATING_CATEGORIES"
              :key="rating.key"
              class="item"
            >
              <div>
                <v-rating
                  v-bind="EVENT_STAR_CONFIG"
                  :value="rating.value"
                  readonly
                />
              </div>
              <div>{{ rating.label }}</div>
            </div>
          </template>
        </div>

        <div class="description">
          {{ feedback.details }}
        </div>
      </div>
    </div>
    <div
      v-if="withLine"
      class="line"
    />
  </div>
</template>

<style lang="scss" scoped>
@import '@/assets/style/mixins';

.wrapper {
  max-width: 865px;
  margin: 0 auto;

  @media (max-width: 920px) {
    width: 90%;
  }
}

.container {
  display: flex;
  gap: 40px;
  align-items: start;
  color: #333;

  @media (max-width: 920px) {
    flex-direction: column;
  }

  .userInfo {
    position: relative;
    display: flex;
    gap: 10px;
    align-items: center;
    width: 230px;

    @media (max-width: 920px) {
      width: 100%;
    }

    .imageContainer {
      position: relative;

      .image {
        max-width: 48px;
        max-height: 48px;
        object-fit: cover;
        border-radius: 100%;
      }

      .like {
        position: absolute;
        right: -2px;
        bottom: -2px;
        display: flex;
        align-items: center;
        justify-content: center;
        width: 24px;
        height: 24px;
        padding: 4px;
        background: #8c7328;
        border: 1px solid #ddd;
        border-radius: 100%;
      }
    }

    .name {
      @include font-h4;

      font-weight: 700;
    }

    .date {
      font-size: 12px;
    }
  }

  .content {
    flex: 1;
  }

  .items {
    display: flex;
    flex-direction: column;
    gap: 20px;
    margin-bottom: 12px;

    .star {
      gap: 5px;

      :deep(button) {
        padding: 0;
      }
    }

    .header {
      display: flex;
      gap: 10px;
      align-items: center;
      justify-content: space-between;

      button {
        display: flex;
        align-items: center;
        color: #000;
      }
    }

    .item {
      display: flex;
      gap: 10px;
      align-items: center;

      @include font-body2;
    }

    .description {
      @include font-body1;
    }
  }
}

.line {
  width: 100%;
  height: 1px;
  margin: 20px 0;
  background: #d9d9d9;
}
</style>
